import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { chatCommunicationController } from '../../../../..//controllers/chatCommunication/ChatCommunicationControllerConnector';
import { useLiveSupportMessagesFetcher } from '../../../../../store/apiHooks/useLiveSupportMessagesFetcher';
import { useCoreDispatch } from '../../../../../store/CoreStore';
import { useChatSlice } from '../../../../../store/slices/chat/selectors';
import { chatActions } from '../../../../../store/slices/chat/slice';
import { ChatTabType, Message } from '../../../../../store/slices/chat/types';
import { useConfigSlice } from '../../../../../store/slices/config/selectors';
import { createFreshChatSubscriptionMessage } from '../../../utils';

import { MessagesWrapper } from './MessagesWrapper';

interface MessagesWrapperConnectedProps {
  messages: Message[];
}

export const MessagesWrapperConnected: FC<MessagesWrapperConnectedProps> = ({ messages }) => {
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const selectedTab = useChatSlice.getSelectedTab();
  const chatSubscriptionState = useChatSlice.getChatSubscriptionState();

  const currentUserId = useConfigSlice.playerConfig()?.userId as string;
  const casinoId = useConfigSlice.session()?.casinoId ?? '';
  const userId = useConfigSlice.playerConfig()?.userId ?? '';
  const tableId = useConfigSlice.session()?.tableId ?? '';

  const liveSupportMessagesFetcher = useLiveSupportMessagesFetcher(false);
  const dispatch = useCoreDispatch();

  const lastMessage = messages[messages.length - 1];
  const isLastMessageFromCurrentUser: boolean = lastMessage && lastMessage.userId === currentUserId;

  // Fetch live support chat messages
  useEffect(() => {
    if (selectedTab === ChatTabType.LiveSupportChat && chatSubscriptionState === 'CHAT_SUBSCRIBED') {
      chatCommunicationController.sendMessage(
        JSON.stringify(createFreshChatSubscriptionMessage(userId, tableId, casinoId))
      );
      liveSupportMessagesFetcher(); // TODO: Investigate if re-fetch needed on every tab switch
    }
  }, [casinoId, chatSubscriptionState, liveSupportMessagesFetcher, selectedTab, tableId, userId]);

  // Handle read status for live support chat
  useEffect(() => {
    if (selectedTab === ChatTabType.LiveSupportChat && (isScrolledToBottom || isLastMessageFromCurrentUser)) {
      dispatch(chatActions.setLiveSupportReadStatus(true));
    }
  }, [dispatch, isLastMessageFromCurrentUser, isScrolledToBottom, selectedTab]);

  const handleScrollToBottom = useCallback((isScrolledToBottom: boolean): void => {
    setIsScrolledToBottom(isScrolledToBottom);
  }, []);

  return <MessagesWrapper messages={messages} currentUserId={currentUserId} onScrollToBottom={handleScrollToBottom} />;
};
